// src/components/desk/CobrokePanel.jsx — the other agency's agent, and the fee split
// with them (0449).
//
// Owner, 2026-08-06: "the agent is going on a meeting where the representative of the
// client is another agent from another agency. In this case they split the commission
// with them on the initial amount... allow our agent to add the representative
// themselves, because we don't have the client's contact information."
//
// ONE renderer, two surfaces — the create modal and CoreMeetingDetail. That is not tidiness:
// the Desk has been bitten repeatedly by a second copy of a list (three separate bugs in one
// day, MEETINGS_MODEL "Surfaces that had their own copy of something"), and a split shown two
// ways is a split two people read differently.
//
// ⚠️ THE ARITHMETIC IS NOT HERE. It lives in `core/cobroke-rules.mjs` with 21 tests, because
// it decides about MONEY and this file cannot be tested. Nothing below recomputes a share;
// it calls splitAmounts/ourShareOf and renders what comes back.

import { useEffect, useRef, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from './../Icon.jsx'
import { input, btn, pill } from './primitives.jsx'
import { searchExternalAgents, createExternalAgent, getSchedulerRoster } from '../../db/core.js'
import { getConfig } from '../../db/index.js'
import { splitAmounts } from '../../../core/cobroke-rules.mjs'

// The agency's standard share, from `config.cobroke_default_percent`.
//
// Cached for the session in a module-level promise: every meeting modal would otherwise
// re-read the config table on open, and the value changes about once a year. A failed read
// falls back to 50 rather than blocking the panel — but note what that fallback IS: the
// last-resort floor for an unreachable config, NOT a second definition of the policy. If
// you find yourself wanting to change it here, change the config key instead.
let _cobrokePromise = null
export function useCobrokeDefault() {
  const [pct, setPct] = useState(50)
  useEffect(() => {
    _cobrokePromise ??= getConfig().catch(() => null)
    let gone = false
    _cobrokePromise.then(c => {
      if (gone) return
      const v = Number(c?.cobroke_default_percent?.us)
      if (Number.isFinite(v) && v > 0 && v <= 100) setPct(v)
    })
    return () => { gone = true }
  }, [])
  return pct
}

// The agent roster, for the "from our agency" tier. Session-cached like the default above:
// the list is short, changes rarely, and every meeting modal would otherwise re-fetch it.
//
// ⚠️ Falls back to [] on failure, which renders the tier EMPTY rather than absent — an
// empty searchable list says "nobody matched", while a missing tier says "this is not
// possible here", and only one of those is true when a fetch fails.
// ⚠️ ACTIVE AGENTS ONLY (owner 2026-08-15). A resigned agent cannot be given a client,
// sent to a viewing, or handed a share of a fee, so offering their name is offering a
// control that must not work — and on a roster this long the leaver sits between two
// colleagues with nothing to say they have left. `is_active !== false` is the app's
// standing test (AgentsTab, AttendanceTab, ScheduleTab, AdminTab all use it); the
// server's roster endpoint publishes the column, so this filters real rows rather
// than silently passing everything.
//
// ⚠️ COMPANY-WIDE ON PURPOSE — DO NOT SCOPE THIS TO THE VIEWER'S OFFICE (owner ruling
// 2026-08-19: "managers and agents must be able to see and pick other offices' active agents
// and tag them in their created meetings"). Asked and answered explicitly, because I had
// proposed exactly that narrowing as one way to stop a cross-office addition widening who may
// later edit the meeting. It was declined: staffing a viewing across offices is ordinary work
// here, not an exception to be made awkward.
//
// The consequence was accepted with it, and is the reason the two rules on the server exist:
// adding a colleague from another office puts them on the meeting, which lets THEM change its
// status (anyone on a meeting may), and lets THEIR manager change it too (core_meeting.js's
// PATCH gate) — and, since the second ruling the same day, lets that manager SEE it in their
// nudge feed rather than holding a right with no door.
//
// The one filter that DOES belong here is activity, below: a resigned colleague cannot take a
// viewing in any office.
export const isActiveAgent = a => a?.is_active !== false

let _rosterPromise = null
export function useRoster(supplied) {
  const [rows, setRows] = useState(() => (supplied || []).filter(isActiveAgent))
  useEffect(() => {
    if (supplied && supplied.length) { setRows(supplied.filter(isActiveAgent)); return }
    _rosterPromise ??= getSchedulerRoster().catch(() => [])
    let gone = false
    _rosterPromise.then(r => { if (!gone) setRows((r || []).filter(isActiveAgent)) })
    return () => { gone = true }
  }, [supplied])
  return rows
}

// ── Picking an agent at another agency ──────────────────────────────────────
// Three sources in one panel, in the owner's own order of trust:
//   1. parties we have worked with before — a real relationship, and already an identity
//   2. the scraped agency-roster dictionary (0204) — a name we saw on a portal
//   3. hand entry — everything the first two missed
//
// ⚠️ A directory row has NO party_id. Picking one calls createExternalAgent, which resolves
// it to a party by PHONE server-side — so two of our agents picking the same person from the
// dictionary land on one identity rather than two rivals.
export function ExternalAgentPick({ onPick, onCancel }) {
  const [q, setQ] = useState('')
  const [res, setRes] = useState({ known: [], directory: [] })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [manual, setManual] = useState(null)   // { name, phone, agency } | null
  // The search's own state, so the panel can tell "still looking" from "nothing matched".
  // Without it a search for an outsider we have never seen rendered the input, then
  // NOTHING, then the manual door — a blank that read as broken (need-care triage T12).
  const [searching, setSearching] = useState(false)
  // Debounced, and the response is dropped when a newer keystroke has already been sent —
  // without the guard a slow early request lands last and overwrites the current results
  // with stale ones.
  const seq = useRef(0)
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setRes({ known: [], directory: [] }); setSearching(false); return }
    const mine = ++seq.current
    setSearching(true)
    const h = setTimeout(() => {
      searchExternalAgents(term)
        .then(r => { if (mine === seq.current) { setRes(r || { known: [], directory: [] }); setSearching(false) } })
        .catch(() => { if (mine === seq.current) { setRes({ known: [], directory: [] }); setSearching(false) } })
    }, 250)
    return () => clearTimeout(h)
  }, [q])
  const searchedEmpty = q.trim().length >= 2 && !searching
    && !(res.known?.length) && !(res.directory?.length)

  async function resolve({ name, phone, agency, source }) {
    setBusy(true); setErr(null)
    try {
      const out = await createExternalAgent({ name, phone, agency, source })
      onPick?.({ party_id: out.party_id, name: out.name, agency: out.agency, is_external: true })
    } catch (e) {
      // The 409 is a real answer, not a failure: the phone belongs to a colleague. Say the
      // sentence that tells the agent what to do instead of showing them a status code.
      setErr(/own agents/i.test(e.message) ? t('meeting_rep_external_is_ours') : e.message)
    } finally { setBusy(false) }
  }

  return (
    <div className="mt-1 rounded-lg border border-border bg-background/60 px-2 py-2 space-y-1.5">
      <input className={input + ' text-xs'} autoFocus value={q} onChange={e => setQ(e.target.value)}
        placeholder={t('meeting_rep_external_search_ph')} />

      {err && <p className="text-[11px] text-red-500 px-1">{err}</p>}

      {/* "Still looking" and "nothing matched" are different answers, and the manual door
          is the ANSWER to the second one — so the empty state points at it instead of
          leaving a silent gap between the input and the + button. */}
      {searching && <p className="text-[11px] text-muted-foreground px-1">…</p>}
      {searchedEmpty && !manual && (
        <p className="text-[11px] text-muted-foreground px-1">{t('meeting_rep_external_none')}</p>
      )}

      {res.known?.length > 0 && (
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-1">{t('meeting_rep_external_known')}</div>
          {res.known.map(p => (
            <button key={p.party_id} disabled={busy}
              onClick={() => onPick?.({ party_id: p.party_id, name: p.name, agency: p.agency, is_external: true })}
              className="w-full text-left px-1.5 py-1 rounded hover:bg-muted text-xs">
              <span className="font-medium">{p.name}</span>
              {p.agency && <span className="text-muted-foreground"> · {p.agency}</span>}
              {p.phone && <span className="text-muted-foreground"> · {p.phone}</span>}
            </button>
          ))}
        </div>
      )}

      {res.directory?.length > 0 && (
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-1">{t('meeting_rep_external_directory')}</div>
          {res.directory.map(d => (
            <button key={d.phone} disabled={busy}
              onClick={() => resolve({ name: d.name || d.phone, phone: d.phone, agency: d.agency, source: 'directory' })}
              className="w-full text-left px-1.5 py-1 rounded hover:bg-muted text-xs">
              <span className="font-medium">{d.name || d.phone}</span>
              {d.agency && <span className="text-muted-foreground"> · {d.agency}</span>}
              {/* The ad count is why we believe they are an agent and not an owner (0204) —
                  it is evidence, so it is shown rather than silently trusted. */}
              {d.ads > 0 && <span className="text-muted-foreground"> · {tLog('meeting_rep_external_ads', { n: d.ads })}</span>}
            </button>
          ))}
        </div>
      )}

      {manual ? (
        <div className="space-y-1 pt-1 border-t border-border/60">
          <input className={input + ' text-xs'} placeholder={t('meeting_rep_external_name_ph')}
            value={manual.name} onChange={e => setManual({ ...manual, name: e.target.value })} />
          <input className={input + ' text-xs'} placeholder={t('meeting_rep_external_phone_ph')} inputMode="tel"
            value={manual.phone} onChange={e => setManual({ ...manual, phone: e.target.value })} />
          <input className={input + ' text-xs'} placeholder={t('meeting_rep_external_agency_ph')}
            value={manual.agency} onChange={e => setManual({ ...manual, agency: e.target.value })} />
          <div className="flex gap-1">
            {/* Both fields required, and the phone is the load-bearing one: it is how the
                server avoids minting a second Giorgi next month. */}
            <button className={btn('primary') + ' text-xs flex-1'}
              disabled={busy || !manual.name.trim() || !manual.phone.trim()}
              onClick={() => resolve({ ...manual, source: 'declared' })}>{t('meeting_rep_external_save')}</button>
            <button className={btn('ghost') + ' text-xs'} onClick={() => setManual(null)}>×</button>
          </div>
        </div>
      ) : (
        <button className="text-[11px] text-primary underline decoration-dotted px-1"
          onClick={() => setManual({ name: q.trim(), phone: '', agency: '' })}>
          + {t('meeting_rep_external_add')}
        </button>
      )}
      {onCancel && (
        <button className="text-[11px] text-muted-foreground px-1" onClick={onCancel}>×</button>
      )}
    </div>
  )
}

// One number field that holds its keystrokes locally and reports only on commit
// (blur / Enter). Re-syncs from the prop when not being edited, so a server-adjusted
// value still lands in the box.
function SplitPercentInput({ value, onCommit }) {
  const [draft, setDraft] = useState(null)   // null = not editing; string while typing
  const commit = () => { if (draft !== null) { onCommit(draft); setDraft(null) } }
  return (
    <input className={input + ' text-xs w-20 tabular-nums'} inputMode="decimal"
      value={draft ?? value}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit() } }} />
  )
}

// ── The split ───────────────────────────────────────────────────────────────
// `value` is the server's shape: [{side, party_id, percent, name?, agency?}] — [] means no
// co-broke on this deal, which is the truth for the great majority of them.
//
// `gross` is the fee from the cooperation, and may be null: an agent often agrees the ratio
// before the fee is settled. The ratio is what they agreed, so it saves either way and the
// amounts appear when the fee does. Showing 0 there would be a lie about money.
//
// `defaultPercent` is the agency standard from config, COPIED into a new row and then owned
// by the deal. This component never re-reads it for an existing split — see the config key's
// comment for what a live read would do to closed deals.
// confirmDestructive: set by surfaces whose onChange WRITES THROUGH to the server (the
// meeting detail modal), so clearing asks first. The create modal leaves it off — there the
// same button is local state and a confirmation would be a toll on an undo.
export function CobrokeSplit({ value = [], gross = null, currency = '', defaultPercent = 50,
                              counterparty = null, onChange, readOnly = false,
                              confirmDestructive = false }) {
  const rows = value || []
  const us = rows.find(r => r.side === 'us') || null
  const externals = rows.filter(r => r.side === 'external')

  // More than two parties is legal in the rules and has no editor here — the owner
  // described one counterparty. Rather than silently drop the extra rows (or worse, let a
  // two-field editor rewrite a three-way agreement), show them and say who may change it.
  const tooMany = externals.length > 1

  const amounts = splitAmounts(gross, rows)
  const amountOf = r => {
    const hit = amounts.find(a => a.side === r.side && a.party_id === r.party_id)
    return hit?.amount ?? null
  }
  const money = n => `${Number(n).toLocaleString('en-US')}${currency ? ' ' + currency : ''}`

  function setOurs(pct) {
    const n = Math.max(0, Math.min(100, Number(pct)))
    if (!Number.isFinite(n)) return
    const other = externals[0]
    if (!other) return
    // The two halves are ONE number: typing ours sets theirs, so the pair can never be
    // saved at 90% or 110%. The rules module rejects those anyway — this stops the agent
    // ever meeting the rejection.
    onChange?.([
      { side: 'us', party_id: null, percent: n },
      { ...other, percent: Math.round((100 - n) * 100) / 100 },
    ])
  }

  // The agency standard, as a pair. `defaultPercent` is COPIED from config into the row and
  // then owned by the deal — never re-read later, or moving the standard would re-price every
  // deal already closed.
  const standardPair = () => (counterparty ? [
    { side: 'us', party_id: null, percent: defaultPercent },
    { side: 'external', party_id: counterparty.party_id, name: counterparty.name,
      agency: counterparty.agency || null,
      percent: Math.round((100 - defaultPercent) * 100) / 100 },
  ] : [])

  if (!rows.length) {
    // ⚠️ WITH A KNOWN OUTSIDER, "no split" IS THE WRONG DEFAULT (owner 2026-09-02: "it should
    // be 50% 50% as default"). This used to read „არ იყოფა — მთელი საკომისიო ჩვენია" beside a
    // representative we have just labelled as another agency's — an assertion that we keep
    // everything, on a deal we know we do not. The standard is shown instead, as the proposal
    // it is, one tap from being recorded.
    //
    // ⚠️ It is NOT auto-saved. A ratio is an agreement between two agencies and writing one
    // nobody typed would put a number in the money log that no human ever agreed — and it
    // would start this field's 15-minute correction clock without anybody acting. So the
    // default is offered, prominently, and recording it stays a decision.
    if (counterparty && !readOnly) {
      const pair = standardPair()
      return (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">{tLog('meeting_split_standard_hint', { n: defaultPercent })}</div>
          <div className="space-y-0.5 opacity-70">
            {pair.map((r, i) => (
              <div key={r.party_id || `us-${i}`} className="flex justify-between gap-2 text-xs">
                <span className="truncate">{r.side === 'us' ? t('meeting_split_us') : (r.name || t('meeting_rep_external_badge'))}</span>
                <span className="shrink-0 tabular-nums">
                  {r.percent}%{splitAmounts(gross, pair).find(a => a.side === r.side)?.amount != null
                    && <span className="text-muted-foreground"> · {money(splitAmounts(gross, pair).find(a => a.side === r.side).amount)}</span>}
                </span>
              </div>
            ))}
          </div>
          <button className={btn('primary') + ' text-xs'} onClick={() => onChange?.(pair)}>
            {t('meeting_split_record_standard')}
          </button>
        </div>
      )
    }
    return (
      <div className="text-xs text-muted-foreground">
        {t('meeting_split_none')}
        {/* Why the button is dead, stated instead of left to be discovered — a control that
            does nothing and says nothing is the complaint this Desk keeps recording. */}
        {!readOnly && !counterparty && (
          <div className="text-[11px] text-muted-foreground/80 mt-0.5">{t('meeting_split_needs_party')}</div>
        )}
      </div>
    )
  }

  if (tooMany || readOnly) {
    return (
      <div className="space-y-0.5 text-xs">
        {rows.map((r, i) => (
          <div key={r.party_id || `us-${i}`} className="flex justify-between gap-2">
            <span className="truncate">
              {r.side === 'us' ? t('meeting_split_us') : (r.name || t('meeting_rep_external_badge'))}
            </span>
            <span className="shrink-0 tabular-nums">
              {r.percent}%{amountOf(r) != null && <span className="text-muted-foreground"> · {money(amountOf(r))}</span>}
            </span>
          </div>
        ))}
        {tooMany && <div className="text-[11px] text-muted-foreground">{tLog('meeting_split_many', { n: rows.length })}</div>}
      </div>
    )
  }

  const them = externals[0]
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground w-16 shrink-0">{t('meeting_split_us')}</label>
        {/* Draft-commit, not write-through (fixed 2026-08-15). The detail modal wires
            onChange straight to a server PATCH, so a live-typed value fired one PATCH
            PER KEYSTROKE — typing "40" saved 4 first — while the input echoed the
            parent's stale snapshot and could swallow the second digit. The draft is
            local until blur/Enter; only the committed number reaches onChange. */}
        <SplitPercentInput value={us?.percent ?? ''} onCommit={setOurs} />
        <span className="text-xs text-muted-foreground">%</span>
        {amountOf(us || {}) != null && (
          <span className="text-xs text-muted-foreground truncate">{money(amountOf(us))}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground w-16 shrink-0 truncate" title={them?.name || ''}>
          {them?.name || t('meeting_rep_external_badge')}
        </label>
        {/* Derived, not typed. One editable field per pair — two would let them disagree,
            and then someone has to decide which is right. */}
        <span className="text-xs tabular-nums w-20">{them?.percent}%</span>
        {amountOf(them) != null && (
          <span className="text-xs text-muted-foreground truncate">{money(amountOf(them))}</span>
        )}
      </div>
      {gross == null && <div className="text-[11px] text-muted-foreground">{t('meeting_split_no_fee')}</div>}
      {us && Number(us.percent) === Number(defaultPercent) && (
        <div className="text-[11px] text-muted-foreground">{tLog('meeting_split_default_note', { n: defaultPercent })}</div>
      )}
      {/* ⚠️ ONE CONTROL, TWO CONSEQUENCES (confirm added 2026-08-15). In the create modal
          `onChange` is local state and this is a harmless undo; in the detail modal it is
          `applySplit`, an immediate PATCH that DELETES the agreed ratio server-side — the
          ratio the close then uses to decide what reaches payroll. Muted 11px underlined
          text, no confirmation, no undo. `confirmDestructive` is passed only by the surface
          that writes, so the create modal keeps its one-tap undo. */}
      <button className="text-[11px] text-muted-foreground underline decoration-dotted"
        onClick={() => {
          if (confirmDestructive && value.length && !window.confirm(t('meeting_split_clear_confirm'))) return
          onChange?.([])
        }}>{t('meeting_split_clear')}</button>
    </div>
  )
}
